import { useMsal } from '@azure/msal-react';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popup } from 'component-library';
import _ from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../contexts/AppContext';
import { BoatModel, User } from '../../../models/ApiModels';
import { GetUser } from '../../../services/UserService';

import useRequest from '../../../utils/net/useRequest';
import PermissionsGate, { ROLES } from '../../../utils/PermissionGate';
import CreateBoatModelInformation from './CreateBoatModelInformation';
import CreateBoatModelSpecifications from './CreateBoatModelSpecifications';

function BoatModelCreate() {
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { currentOemTenantId, isHumphreeHome } = useContext(AppContext);
  const boatModels = useSelector((state: any) => state.boatModels.boatModels);
  const navigate = useNavigate();
  const [showHasChangesPopup, setShowHasChangesPopup] = useState(false);
  const [page, setPage] = useState(0);

  const { accounts } = useMsal();
  const account = accounts[0];
  const [user = {}, isUserLoading, userGetError] = useRequest<User>(
    () => GetUser(account?.idTokenClaims?.extension_oemUserId as string),
    [account]
  );
  const userPrefersImperial = useMemo(() => {
    return user.settings?.measurementSystem === 'Imperial';
  }, [user.settings]);

  const initBoatModel: BoatModel = {
    flyBridgeOption: false,
    id: '',
    name: '',
    make: '',
    oemTenantId: '',
    length: undefined,
    beam: undefined,
    dryWeight: undefined,
    topSpeed: undefined,
    cruisingSpeed: undefined,
    propulsionSystemIds: [],
    status: 'Public',
    createdDate: undefined,
    availableConfigurationIds: [],
  };

  const [newBoatModel, setNewBoatModel] = useState<BoatModel>(initBoatModel);

  useEffect(() => {
    if (!isHumphreeHome) {
      setNewBoatModel((prev) => ({ ...prev, oemTenantId: currentOemTenantId }));
    }
  }, [currentOemTenantId]);

  const stepsComponentList = [
    <CreateBoatModelInformation
      key={page}
      newBoatModel={newBoatModel}
      setNewBoatModel={(boatModel: BoatModel) => setNewBoatModel({ ...boatModel })}
      boatModels={boatModels}
      page={page}
      setPage={(value: number) => setPage(value)}
      GoBack={() => GoBack()}
    />,
    <CreateBoatModelSpecifications
      key={page}
      newBoatModel={newBoatModel}
      setNewBoatModel={(boatModel: BoatModel) => setNewBoatModel({ ...boatModel })}
      page={page}
      setPage={(value: number) => setPage(value)}
      GoBack={() => GoBack()}
      userPrefersImperial={userPrefersImperial}
    />,
  ];

  useEffect(() => {
    if (_.isEqual(initBoatModel, newBoatModel)) setHasChanges(false);
    else {
      setHasChanges(true);
    }
  }, [newBoatModel]);

  function GoBack() {
    if (hasChanges) {
      setShowHasChangesPopup(true);
    } else {
      navigate(-1);
    }
  }

  return (
    <PermissionsGate
      roles={[ROLES.admin, ROLES.editor, ROLES.oemAdmin, ROLES.oemEditor]}
      rejected={() =>
        setTimeout(() => {
          navigate('/models');
        }, 100)
      }
    >
      <div className='h-full flex flex-col'>
        <h1 className='flex flex-wrap items-center mb-10 md:prose-heading4 2xl:prose-heading3 gap-1'>
          <div className='cursor-pointer' onClick={() => GoBack()}>
            Boat models
          </div>
          <FontAwesomeIcon icon={faChevronRight} size='sm' />
          <div>Add new boat model</div>
        </h1>
        <div className='h-full'>
          {stepsComponentList[page]}
          {loading && (
            <div className='w-full flex justify-center items-center h-full'>
              <div className='w-12 h-12 border-l-2 border-primary-400 rounded-full animate-spin'></div>
            </div>
          )}
        </div>
        {showHasChangesPopup && (
          <Popup
            confirm={() => navigate('/models')}
            close={() => setShowHasChangesPopup(false)}
            heading='Discard boat model?'
            paragraph='You will lose all the information entered about this boat model.'
            status='error'
            cancelText='Keep it'
            confirmText='Discard boat model'
          />
        )}
      </div>
    </PermissionsGate>
  );
}

export default BoatModelCreate;
